import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { ReservationSyncDto } from "src/features/things/reservationSyncApi"
import { LoadedReport } from "./useReservationSyncReport"

/**
 * The v1/v2 badge.
 *
 * Deliberately loud and repeated on every surface that shows a run. The two
 * partner APIs are read through different endpoints — v1 has a per-locker
 * reservations endpoint that reports a total, v2 has none and must be bucketed out
 * of one global pass — so a reader who does not know the version cannot judge the
 * report's completeness. Colour alone would not carry it, hence the text.
 */
export const VersionBadge: FC<{ version: "v1" | "v2"; size?: "small" | "medium" }> = ({
  version,
  size = "small",
}) => (
  <Chip
    size={size}
    label={`SST ${version.toUpperCase()}`}
    variant={version === "v1" ? "outlined" : "filled"}
    sx={{
      fontWeight: 700,
      letterSpacing: "0.06em",
      borderRadius: 1,
      ...(version === "v2"
        ? { bgcolor: "secondary.main", color: "common.black" }
        : { borderColor: "primary.main", color: "primary.main" }),
    }}
  />
)

const STATUS_LABEL: Record<string, string> = {
  DRIFT_ASSESSED: "drift assessed",
  SYNC_APPLIED: "sync applied",
  SYNC_REVERTED: "sync reverted",
  SYNC_FAILED: "sync failed",
}

export const RunStatusChip: FC<{ status: ReservationSyncDto["status"] }> = ({
  status,
}) => (
  <Chip
    size="small"
    label={STATUS_LABEL[status] ?? status}
    color={
      status === "SYNC_APPLIED"
        ? "success"
        : status === "SYNC_FAILED"
          ? "error"
          : status === "SYNC_REVERTED"
            ? "default"
            : "primary"
    }
    variant={status === "DRIFT_ASSESSED" ? "outlined" : "filled"}
  />
)

/**
 * `readState` is shown next to every count it qualifies.
 *
 * A `FAILED` or `UNVERIFIED` read renders numerically identical to a clean one —
 * zero drift — so a count without its read state is a number nobody should act on.
 */
export const ReadStateChip: FC<{ readState: ReservationSyncDto["readState"] }> = ({
  readState,
}) => (
  <Chip
    size="small"
    variant="outlined"
    label={readState.toLowerCase()}
    color={
      readState === "COMPLETE"
        ? "success"
        : readState === "FAILED"
          ? "error"
          : "warning"
    }
  />
)

/**
 * The run verdict — the page's top line (LOCKER-421 `DoD10`).
 *
 * Three levels, never two. A strict all-`COMPLETE` rule would leave every v2
 * locker permanently failing, because SST v2 reports no per-locker total — and a
 * gate whose every case reads the same is not a gate.
 *
 * `QUALIFIED` is the only place this console uses an amber wash, and it is used as
 * a warning rather than as decoration: the text is `#7A5312` on `#FBF3E4`, which
 * clears AA, and the alternative (a neutral chip) would make "counts you may act
 * on with a stated caveat" look like "counts you may act on".
 */
export const VerdictChip: FC<{
  verdict: ReservationSyncDto["verdict"]
  size?: "small" | "medium"
}> = ({ verdict, size = "small" }) => {
  if (!verdict)
    // A run stored before the verdict existed has no honest value for it, and a
    // guessed one would be a claim about reads nobody recorded.
    return (
      <Chip
        size={size}
        variant="outlined"
        label="verdict not recorded"
        sx={{ color: "text.disabled", borderStyle: "dashed" }}
      />
    )
  return (
    <Chip
      size={size}
      label={`verdict  ${verdict}`}
      color={
        verdict === "ACTIONABLE"
          ? "success"
          : verdict === "QUALIFIED"
            ? "warning"
            : "error"
      }
      variant={verdict === "QUALIFIED" ? "outlined" : "filled"}
      sx={{ fontWeight: 700, letterSpacing: "0.06em", borderRadius: 1 }}
    />
  )
}

/**
 * A count, or an em dash when the run refused to state one.
 *
 * The em dash is deliberate and is NOT a zero. A zero here reads as "no drift
 * found", which is the exact wrong conclusion to draw from a run where a source
 * was truncated or failed. Every number on an INCONCLUSIVE run goes through this.
 */
export const fmtCount = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : n.toLocaleString()

export const fmtBytes = (b: number): string =>
  b >= 1048576
    ? `${(b / 1048576).toFixed(1)} MB`
    : b >= 1024
      ? `${Math.round(b / 1024)} KB`
      : `${b} B`

export const fmtNum = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : n.toLocaleString()

/**
 * An ISO instant as `YYYY-MM-DD HH:mm` in the VIEWER's timezone (LOCKER-437).
 *
 * Built from the local-time getters rather than `toLocaleString()` so the shape is
 * the same sortable one for every viewer — only the clock is theirs. The full
 * locale rendering goes in the cell's tooltip.
 */
export const fmtLocalDateTime = (iso: string): string => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  const p = (n: number) => String(n).padStart(2, "0")
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}`
  )
}

/**
 * The part-by-part loader every report tab shows.
 *
 * It exists so a partially-loaded report can never read as a complete one: the
 * counts above it describe the rows fetched so far, and this line says how many
 * that is out of how many.
 */
export const PartLoader: FC<{
  report: LoadedReport<unknown>
  onLoadMore: () => void
  label: string
}> = ({ report, onLoadMore, label }) => {
  if (!report.partsTotal) return null
  const done = report.partsLoaded >= report.partsTotal
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {label}: part {report.partsLoaded} of {report.partsTotal} ·{" "}
          {fmtNum(report.rows.length)} row(s) · {fmtBytes(report.bytesLoaded)} read
        </Typography>
        {!done && (
          <Typography
            component="button"
            onClick={onLoadMore}
            disabled={report.loading}
            variant="caption"
            sx={{
              border: "none",
              background: "none",
              color: "primary.main",
              fontWeight: 600,
              cursor: report.loading ? "default" : "pointer",
              p: 0,
            }}
          >
            {report.loading ? "loading…" : "load next part"}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant={report.loading ? "indeterminate" : "determinate"}
        value={(report.partsLoaded / report.partsTotal) * 100}
        sx={{ height: 4, borderRadius: 2 }}
      />
      {report.error && (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {report.error}
        </Typography>
      )}
    </Box>
  )
}
